import { useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { PLAN_PHRASES, FALLBACK_MINUTES } from '@/constants/reading-tracker';
import type { BookView, GoalType } from '@/types/api';

/**
 * Screen 4 — render the computed plan and confirm.
 *
 * `totalUnits` and `totalDays` are computed here for preview purposes
 * only. The server re-computes them on `POST /reading-goals` from the same
 * EFFECTIVE counts (`effectiveCounts` = the account's override ?? catalogue),
 * so the displayed values match what gets persisted.
 */
export interface SummaryScreenProps {
  book: BookView;
  /** Effective page/chapter counts (account override ?? catalogue). */
  effectiveCounts: { pages: number | null; chapters: number | null };
  goalType: GoalType;
  perDay: number;
  onConfirm: () => void;
  onBack: () => void;
  /** True while the create-goal POST is in flight. Disables the CTA. */
  submitting?: boolean;
}

function unitsFor(
  counts: { pages: number | null; chapters: number | null },
  type: GoalType,
): number {
  switch (type) {
    case 'chapters':
      return counts.chapters ?? 0;
    case 'pages':
      return counts.pages ?? 0;
    case 'minutes':
      return counts.pages ?? FALLBACK_MINUTES;
  }
}

function unitIcon(type: GoalType): string {
  return type === 'chapters' ? '📑' : type === 'pages' ? '📄' : '⏱️';
}

export function SummaryScreen({
  book,
  effectiveCounts,
  goalType,
  perDay,
  onConfirm,
  onBack,
  submitting = false,
}: SummaryScreenProps) {
  const totalUnits = unitsFor(effectiveCounts, goalType);
  const totalDays = totalUnits > 0 ? Math.ceil(totalUnits / perDay) : 0;

  // Pick a phrase once per render — re-mounts get a fresh roll, but
  // re-renders during stepper changes don't (only goalType / perDay /
  // totalDays influence the seed).
  const highlight = useMemo(() => {
    const idx = Math.floor(Math.random() * PLAN_PHRASES.length);
    // PLAN_PHRASES is non-empty (declared as a literal), but TS narrows
    // an indexed access to `T | undefined` regardless. Fall back to the
    // first phrase in the unreachable case.
    const phrase = PLAN_PHRASES[idx] ?? PLAN_PHRASES[0]!;
    return phrase({ perDay, goalType, totalDays });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perDay, goalType, totalDays]);

  return (
    <section className="rounded-3xl border border-or-slate-200 bg-white p-6 shadow-sm">
      <header className="text-center">
        <h2 className="font-display text-[24px] font-bold text-or-slate-900">
          🗺️ Your Reading Plan
        </h2>
      </header>

      {/* Dark "plan at a glance" panel. Uses the existing slate-700 token
          rather than introducing a new dark gradient. */}
      <div className="relative mt-6 overflow-hidden rounded-3xl bg-or-slate-700 px-6 py-7 text-white">
        <span
          aria-hidden
          className="pointer-events-none absolute -right-2 -top-2 text-[60px] opacity-15"
        >
          📚
        </span>
        <h3 className="font-display text-[18px] font-bold text-or-orange-500">
          📋 Your Plan at a Glance
        </h3>
        <dl className="mt-4 flex flex-col">
          <SummaryRow
            label="📗 Book"
            value={book.title}
          />
          <SummaryRow
            label={`${unitIcon(goalType)} Track by`}
            value={goalType.charAt(0).toUpperCase() + goalType.slice(1)}
          />
          <SummaryRow
            label="🎯 Per day"
            value={`${perDay} ${goalType}`}
          />
          <SummaryRow
            label="📏 Total in book"
            value={`${totalUnits} ${goalType}`}
          />
          <SummaryRow
            label="📅 Days to finish"
            value={`${totalDays} days`}
            highlight
          />
        </dl>
      </div>

      {/* Encouragement highlight box */}
      <div className="mt-4 flex items-start gap-3 rounded-2xl bg-or-orange-600 px-5 py-4 text-white">
        <span className="text-[24px] leading-none" aria-hidden>
          💡
        </span>
        <p className="text-[14px] font-bold leading-snug">{highlight}</p>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button type="button" variant="secondary" size="md" onClick={onBack} disabled={submitting}>
          ← Back
        </Button>
        <Button type="button" size="lg" onClick={onConfirm} disabled={submitting}>
          {submitting ? 'Saving…' : "Let's Do This! 🚀"}
        </Button>
      </div>
    </section>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  highlight?: boolean;
  small?: boolean;
}

function SummaryRow({ label, value, highlight, small }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/10 py-2 last:border-b-0">
      <dt className={small ? 'text-[12px] opacity-80' : 'text-[13px] opacity-90'}>{label}</dt>
      <dd
        className={
          highlight
            ? 'text-right text-[14px] font-bold text-or-orange-500'
            : small
              ? 'text-right text-[12px] opacity-90'
              : 'text-right text-[14px] font-bold'
        }
      >
        {value}
      </dd>
    </div>
  );
}
